// inlineFeedbackThemeManager.ts
import { CSSProperties } from "react";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
} from "../../tutorial/components/tutorial.types";
import { extractGradientColors } from "../../../../general";

interface InlineFeedbackStyleConfig {
  Form?: CSSProperties;
  Heading?: CSSProperties;
  Description?: CSSProperties;
  ActionContainer?: CSSProperties;
  ActionButton?: CSSProperties;
  ActionSelectedButton?: CSSProperties;
  IconStyle?: CSSProperties;
  SelectedIconStyle?: CSSProperties;
  Footer?: CSSProperties;
  closeIconColor?: string;
}

interface InlineFeedbackThemeConfig {
  backgroundColor?: string;
  borderColor?: string;
  buttonColor?: string;
  fontFamily?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

class InlineFeedbackThemeManager {
  private themeConfig: InlineFeedbackThemeConfig;
  private styleConfig: InlineFeedbackStyleConfig;
  private questThemeData: PartialQuestThemeData;
  private brandTheme: PartialBrandTheme;

  constructor(
    themeConfig: InlineFeedbackThemeConfig = {},
    styleConfig: InlineFeedbackStyleConfig = {},
    questThemeData: PartialQuestThemeData = {
      accentColor: "",
      theme: "",
      borderRadius: "",
      buttonColor: "",
      images: [],
    },
    brandTheme: PartialBrandTheme = {
      accentColor: "",
      background: "",
      borderRadius: "",
      primaryButton: "",
      contentColor: "",
      fontFamily: "",
      logo: "",
      primaryColor: "",
      secondaryColor: "",
      tertiaryColor: "",
      titleColor: "",
    }
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData;
    this.brandTheme = brandTheme;
  }

  getContainerStyles(): CSSProperties {
    const cssVars = {
      "--backgroundColor": this.themeConfig.backgroundColor,
      "--borderColor": this.themeConfig.borderColor,
      "--buttonColor":
        this.brandTheme?.primaryButton ||
        this.questThemeData?.buttonColor ||
        this.themeConfig.buttonColor,
      "--fontFamily":
        this.brandTheme?.contentColor || this.themeConfig.fontFamily,
      "--primaryColor":
        this.brandTheme?.primaryColor || this.themeConfig.primaryColor,
      "--secondaryColor":
        this.brandTheme?.secondaryColor || this.themeConfig.secondaryColor,
      "--borderRadius":
        this.styleConfig.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius || '12px',
    } as CSSProperties;

    return {
      ...this.styleConfig?.Form,
      ...cssVars,
    };
  }

  getHeadingStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor ||
        "#2c2c2c",
      ...this.styleConfig?.Heading,
    };
  }

  getDescriptionStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  getActionStyles() {
    return {
      container: this.styleConfig?.ActionContainer || {},
      button: this.styleConfig?.ActionButton || { background: this.brandTheme?.primaryColor || this.questThemeData?.accentColor, color: this.brandTheme?.titleColor, borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius, borderColor: this.brandTheme?.primaryColor || this.questThemeData?.accentColor } || {},
      selectedButton: this.styleConfig?.ActionSelectedButton || { background: this.brandTheme?.primaryColor || this.questThemeData?.accentColor, color: this.brandTheme?.titleColor } || {},
      icon: this.styleConfig?.IconStyle || {},
      selectedIcon: this.styleConfig?.SelectedIconStyle || {},
    };
  }

  getFooterStyles(): CSSProperties {
    return this.styleConfig?.Footer || { borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}` };
  }

  getCloseIconColor(): string {
    return this.styleConfig?.closeIconColor || "#000000";
  }

  updateThemeConfig(newConfig: Partial<InlineFeedbackThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<InlineFeedbackStyleConfig>): void {
    this.styleConfig = { ...this.styleConfig, ...newConfig };
  }

  updateQuestTheme(newTheme: Partial<PartialQuestThemeData>): void {
    this.questThemeData = { ...this.questThemeData, ...newTheme };
  }

  updateBrandTheme(newTheme: Partial<PartialBrandTheme>): void {
    this.brandTheme = { ...this.brandTheme, ...newTheme };
  }

  iconColor() {
    return extractGradientColors(this.brandTheme?.primaryColor || this.questThemeData?.accentColor || this.themeConfig?.primaryColor) || "#2c2c2c";
  }

  successHeadingColor() {
    return this.brandTheme?.titleColor || this.themeConfig?.primaryColor || "#2c2c2c";
  }

  successDescriptionColor() {
    return this.brandTheme?.contentColor || this.themeConfig?.secondaryColor || "#2c2c2c";
  }

  goBtnBorderRadius() {
    return this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || '12px';
  }
}

export default InlineFeedbackThemeManager;
